/**
 * 報酬設定 (commission_sets) の型定義 (v3.2.5 / T5)。
 *
 * 設計方針:
 *  - **バージョンコントロールモデル**: 名前付きセット (例: "2026 標準" / "TimeWaver スペシャル")
 *    を複数作成可能。商品カテゴリ別にどのセットを「有効」にするかを切替える。
 *  - 各セットは 3 種のリストを内包: 紹介者 (referrer) / SA (sa) / 社員 (employee)。
 *  - 各リストは商品マスタ (catalog items) を参照し、商品行ごとに
 *    報酬タイプ (rate or fixed) と値 + 追加金額 + 割引金額を持つ。
 *  - 計算: rate なら `税抜価格 × value/100`、fixed なら `value (円)`。
 *    最終支払額 = 上記 + additionalAmount − discountAmount。
 *
 * 物理コレクション:
 *   commission_sets/{setId}                          ← セット本体
 *   commission_sets/{setId}/lists/{listType}         ← listType ∈ 'referrer' | 'sa' | 'employee'
 *   product_categories/{categoryId}.activeCommissionSetId ← 商品カテゴリ別の有効セット参照
 *
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md v3.2.5
 */
import type { IsoOrTimestamp } from './common';

/** 報酬リストの種別。 */
export type CommissionListType = 'referrer' | 'sa' | 'employee';

export const COMMISSION_LIST_TYPES: readonly CommissionListType[] = [
  'referrer',
  'sa',
  'employee',
] as const;

export const COMMISSION_LIST_LABELS: Record<CommissionListType, string> = {
  referrer: '紹介者',
  sa: 'SA (営業パートナー)',
  employee: '社員 (営業内勤)',
};

/** 報酬タイプ。`rate` = 税抜価格に対する % / `fixed` = 固定円。 */
export type CommissionType = 'rate' | 'fixed';

export const COMMISSION_TYPE_LABELS: Record<CommissionType, string> = {
  rate: 'レート (%)',
  fixed: '固定額 (円)',
};

/**
 * 報酬リスト内の 1 商品行。
 *
 * - `productId` / `catalogId`: 商品マスタ参照 (snapshot)
 * - `productName`: 表示用スナップショット (商品マスタ更新で出ても旧名のまま固定。
 *   設定セットの履歴性を保つため)
 * - `type`: null の場合はリスト全体の `defaultType` を使用 (個別未指定)
 * - `value`: rate なら %、fixed なら円
 * - `additionalAmount`: 上乗せ円 (デフォルト 0)
 * - `discountAmount`: 減額円 (デフォルト 0)
 * - `selfDevelopedAdditionRate`: 開拓顧客加算率 (%) の商品ごとの指定。SA の個別設定 (override) の
 *   items だけで使う。未指定 / null = 個別設定の既定 → セットのカテゴリー別加算率の順に引き継ぐ。
 *   0 は「この商品は加算なし」。解決は `resolveEffectiveSelfDevelopedAdditionRate`。
 */
export interface CommissionItem {
  productId: string;
  catalogId: string;
  productName: string;
  type: CommissionType | null;
  value: number;
  additionalAmount: number;
  discountAmount: number;
  selfDevelopedAdditionRate?: number | null;
}

/**
 * 報酬リスト (commission_sets/{setId}/lists/{listType})。
 * `defaultType` はリスト全体のデフォルト。各 item.type が null なら採用される。
 */
export interface CommissionList {
  listType: CommissionListType;
  defaultType: CommissionType;
  items: CommissionItem[];
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

/**
 * 報酬セット本体 (commission_sets/{setId})。
 *
 * セット単位で「2026年Q1標準セット」「TimeWaver用特別セット」等を管理。
 * リスト本体は subcollection `lists/{referrer|sa|employee}` に分かれる。
 */
export interface CommissionSet {
  name: string;
  description?: string | null;
  /**
   * @deprecated v3.2.23+: ProductCatalog.linkedCommissionSetId 経由で effective を決定するため不要。
   * 後方互換のため型に残すが、UI / Cloud Function での参照は禁止。
   */
  isActive?: boolean;
  /**
   * 季節タグ (v3.2.23+)。SA 報酬表で「通常期 / セール期」の分類表示に使う。
   * effective 判定 (どの報酬セットを使うか) は ProductCatalog.linkedCommissionSetId 経由なので、
   * このフィールドは表示用ラベル兼整合性チェック (本セットを normal カタログに紐付けるか sale か) のみ。
   * undefined は 'normal' として扱う（後方互換）。
   */
  season?: 'normal' | 'sale';
  /** @deprecated v3.2.9 で「セット単位の単一有効化」に統合。`isActive` を使うこと。 */
  activeCategoryCount?: number;
  /**
   * 開拓顧客の SA 報酬加算率 (v3.3.0+)。
   * 主軸カテゴリー ID をキーとする % 値のマップ。
   *   例: { "categoryId-abc": 2 } → そのカテゴリーで弊社紹介フラグの**ない** Deal
   *   (= SA 自身が開拓した顧客) は SA 報酬率に 2% を直接プラスして計算。
   * 弊社紹介フラグのある Deal はベース報酬率のまま (加算なし)。
   * 未指定 / 0 のカテゴリーは加算なし。紹介者・社員報酬には影響しない。
   * UI: CommissionDetailPage の SA タブで編集可能。
   */
  selfDevelopedSaAdditionRates?: Record<string, number>;
  /**
   * @deprecated v3.3.0 で「弊社紹介減額」から「開拓顧客加算」方式に反転。
   * 旧データ互換のため型に残す。読み取りは selfDevelopedSaAdditionRates が
   * 未設定のカテゴリーに限りこの値を加算率として読み替える (フォールバック)。
   */
  companyReferralSaDiscountRates?: Record<string, number>;
  createdAt: IsoOrTimestamp;
  createdBy: string;
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

/**
 * 報酬計算ヘルパーの結果。
 * formula 文字列は UI で「税抜 ¥1,000,000 × 10% + ¥5,000 − ¥0 = ¥105,000」のように表示する用。
 */
export interface CommissionCalcResult {
  productId: string;
  productName: string;
  /** 商品の税抜価格 (計算根拠) */
  basePriceExcludingTax: number;
  type: CommissionType;
  value: number;
  additionalAmount: number;
  discountAmount: number;
  /** 最終支払額 (整数円) */
  finalAmount: number;
  formula: string;
}

/**
 * 報酬計算 — 単一商品行に対して最終支払額を算出する純関数。
 * UI と Cloud Function の両方で利用 (報酬台帳起票 T6)。
 */
/**
 * 報酬台帳エントリ (v3.2.8 / T6 追加)。
 *
 * 物理コレクション (subcollection、recipientType によって書込先が異なる):
 *   - 紹介者: `mail_contacts/{contactId}/referral_records/{recordId}`
 *   - SA / 社員: `admins/{uid}/sales_records/{recordId}`
 *
 * 1 レコード = 1 商品 × 1 受取人 × 1 商談入金。複数商品の商談では商品数 × 受取人種別数のレコードが起票される。
 *
 * status 遷移:
 *   pending → requested (経理へ支払い申請メール送信時) → paid (実際に振込完了を手動マーク)
 */
export type CommissionRecordStatus = 'pending' | 'requested' | 'paid';

export const COMMISSION_RECORD_STATUS_LABELS: Record<CommissionRecordStatus, string> = {
  pending: '未申請',
  requested: '申請中',
  paid: '支払済',
};

export interface CommissionRecord {
  // ─── 受取人 ───
  recipientType: CommissionListType;
  /** referrer なら mail_contacts/{id}、sa/employee なら admins/{uid} */
  recipientId: string;
  recipientName: string;
  recipientEmail?: string | null;

  // ─── 由来商談 ───
  dealId: string;
  dealTitle: string;
  /** 商談の顧客 (購入者) */
  contactId: string;
  contactName: string;
  /** 入金確定日 (paidAt がない場合は Trigger 実行日) */
  paidAt: IsoOrTimestamp;
  /** 派生元の請求書 ID */
  invoiceId?: string | null;
  /** 派生元の見積書 ID */
  quoteId?: string | null;

  // ─── 報酬計算情報 ───
  commissionSetId: string;
  commissionSetName: string;
  /** 報酬対象商品 */
  productId: string;
  productName: string;
  /** 計算根拠の税抜価格 (商品 × 数量の合計) */
  basePriceExcludingTax: number;
  commissionType: CommissionType;
  commissionValue: number;
  additionalAmount: number;
  discountAmount: number;
  /** 最終支払額 (整数円) */
  finalAmount: number;
  /** "税抜 ¥1,000,000 × 5% + ¥1,000 − ¥500 = ¥50,500" の説明文 */
  formula: string;

  // ─── 支払い管理 ───
  status: CommissionRecordStatus;
  /** sa/employee の支払先振込口座 ID (admins.bankAccounts[].id)。referrer は手動 */
  bankAccountId?: string | null;
  /** 支払先口座のスナップショット (経理への申請メール送信時に固定) */
  bankAccountSnapshot?: {
    label?: string | null;
    bankName: string;
    branchName: string;
    accountType: 'ordinary' | 'checking' | 'savings';
    accountNumber: string;
    accountHolder: string;
  } | null;
  /** 支払い申請メール送信日 */
  requestedAt?: IsoOrTimestamp | null;
  /** 支払い完了日 (手動マーク) */
  paidOutAt?: IsoOrTimestamp | null;
  /** 外部 SA からアップロードされた請求書ファイル (Storage URL) */
  invoiceUrl?: string | null;
  /** 編集メモ (任意) */
  notes?: string | null;

  /**
   * 紐づく commission_invoices/{id} (v3.2.14 / Phase 3a)。
   * SA が請求書を発行すると、含まれる records にこの ID が書き込まれ、status='requested' に遷移する。
   * 取消時は null に戻る。referrer / employee は現状 null 固定 (将来 Phase で対応)。
   */
  commissionInvoiceId?: string | null;

  /**
   * 開拓顧客加算情報 (v3.3.0+)。SA レコードで `deal.isCompanyReferral !== true` かつ
   * commission_sets.selfDevelopedSaAdditionRates[primaryCategoryId] > 0 のときに付与。
   * 監査用ログ: 「いつ・どの率で加算されたか」を台帳に残す。
   */
  selfDevelopedAddition?: {
    /** 適用された加算率 (%) */
    rate: number;
    /** 加算前の金額 (= ベース報酬率で計算した finalAmount) */
    baseAmount: number;
    /** 加算により上乗せされた金額 (finalAmount - baseAmount) */
    additionAmount: number;
    /**
     * 加算率の出どころ (2026-11-02+)。item = 個別設定の商品ごとの率 / override = 個別設定の既定 /
     * set = セットのカテゴリー別加算率。これより前のレコードには無い (= set)。
     */
    source?: SelfDevelopedAdditionRateSource;
  } | null;

  /**
   * @deprecated v3.3.0 で「弊社紹介減額」から「開拓顧客加算」方式に反転。
   * 旧レコードの表示互換のため型に残す。新規レコードでは常に null。
   */
  companyReferralDiscount?: {
    /** 適用された減額率 (%) */
    rate: number;
    /** 減額前の金額 (= 通常レートで計算した finalAmount) */
    grossAmount: number;
    /** 減額されて差し引かれた金額 (gross - finalAmount) */
    discountAmount: number;
  } | null;

  createdAt: IsoOrTimestamp;
  createdBy: string;
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

export function calculateCommission(args: {
  basePriceExcludingTax: number;
  item: CommissionItem;
  defaultType: CommissionType;
  /**
   * 開拓顧客加算率 (%) — v3.3.0+ (旧「弊社紹介減額率」の反転)。
   *
   * 指定 (> 0) の場合、**報酬率 (rate) に直接 % を加算する** (乗算ではない)。
   *   例: ベース報酬率 5.5% / 加算率 2% → 実効報酬率 7.5% で計算
   * 加算結果は 100% を上限にクランプ。
   *
   * 固定額タイプ (type='fixed') は本加算の対象外 (報酬率が存在しないため)。
   * Deal.isCompanyReferral !== true (= 開拓顧客) かつ SA レコード起票時のみ
   * 呼び出し側が渡す。弊社紹介フラグのある Deal はベース報酬率のまま。
   */
  selfDevelopedAdditionRate?: number;
}): CommissionCalcResult {
  const { basePriceExcludingTax, item, defaultType, selfDevelopedAdditionRate } = args;
  const type = item.type ?? defaultType;
  const additionRate =
    typeof selfDevelopedAdditionRate === 'number' &&
    selfDevelopedAdditionRate > 0 &&
    selfDevelopedAdditionRate <= 100
      ? selfDevelopedAdditionRate
      : 0;
  // v3.3.0: 報酬率 (rate) に直接加算する方式 (旧: 弊社紹介時に減算)。固定額は加算対象外。
  const effectiveValue =
    type === 'rate' && additionRate > 0 ? Math.min(100, item.value + additionRate) : item.value;
  const base =
    type === 'rate'
      ? Math.round((basePriceExcludingTax * effectiveValue) / 100)
      : Math.round(effectiveValue);
  const finalAmount = Math.max(
    0,
    base + Math.round(item.additionalAmount) - Math.round(item.discountAmount),
  );
  // 数式表示: rate 型で加算が適用された場合は (ベース + 加算 = 実効) を明示。
  const rateExpr =
    type === 'rate' && additionRate > 0
      ? `(${item.value}% + ${additionRate}% 開拓顧客加算 = ${effectiveValue}%)`
      : type === 'rate'
        ? `${item.value}%`
        : '';
  const formula =
    type === 'rate'
      ? `税抜 ¥${basePriceExcludingTax.toLocaleString()} × ${rateExpr} + ¥${item.additionalAmount.toLocaleString()} − ¥${item.discountAmount.toLocaleString()} = ¥${finalAmount.toLocaleString()}`
      : `固定 ¥${item.value.toLocaleString()} + ¥${item.additionalAmount.toLocaleString()} − ¥${item.discountAmount.toLocaleString()} = ¥${finalAmount.toLocaleString()}`;
  return {
    productId: item.productId,
    productName: item.productName,
    basePriceExcludingTax,
    type,
    value: item.value,
    additionalAmount: item.additionalAmount,
    discountAmount: item.discountAmount,
    finalAmount,
    formula,
  };
}

/**
 * カテゴリー別の「開拓顧客加算率」を解決する (v3.3.0+)。
 * selfDevelopedSaAdditionRates が未設定のカテゴリーは、旧「弊社紹介減額率」
 * (companyReferralSaDiscountRates) の値を加算率として読み替える
 * (旧データの再入力を不要にする後方互換フォールバック)。
 * 返値は 0〜100 にクランプ済み。0 = 加算なし。
 */
export function resolveSelfDevelopedAdditionRate(
  set:
    | Pick<CommissionSet, 'selfDevelopedSaAdditionRates' | 'companyReferralSaDiscountRates'>
    | null
    | undefined,
  categoryId: string | null | undefined,
): number {
  if (!set || !categoryId) return 0;
  const raw =
    set.selfDevelopedSaAdditionRates?.[categoryId] ??
    set.companyReferralSaDiscountRates?.[categoryId] ??
    0;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(100, n);
}

/** 開拓顧客加算率をどこから取ったか。 */
export type SelfDevelopedAdditionRateSource = 'item' | 'override' | 'set';

/**
 * 個別設定の加算率の入力値を正規化する。数値なら 0〜100 (小数 2 桁) に丸め、
 * 数値でなければ null (= 指定なし・引き継ぐ) を返す。0 は「加算なし」の指定として残す。
 */
export function normalizeSelfDevelopedAdditionRate(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, Math.round(value * 100) / 100));
}

/**
 * SA 1 人・1 商品に実際にかかる開拓顧客加算率を決める (2026-11-02+)。
 *
 * 優先順:
 *   1. 個別設定 (override) の商品ごとの率 `item.selfDevelopedAdditionRate`
 *   2. 個別設定の既定 `override.selfDevelopedAdditionRate`
 *   3. セットのカテゴリー別加算率 (`resolveSelfDevelopedAdditionRate`)
 * 1・2 は 0 も「加算なし」の指定として扱い、3 へ引き継がない。
 * 弊社紹介の商談・社員・紹介者には加算しない判定は呼び出し側で行う。
 */
export function resolveEffectiveSelfDevelopedAdditionRate(args: {
  set:
    | Pick<CommissionSet, 'selfDevelopedSaAdditionRates' | 'companyReferralSaDiscountRates'>
    | null
    | undefined;
  categoryId: string | null | undefined;
  overrideRate?: number | null;
  itemRate?: number | null;
}): { rate: number; source: SelfDevelopedAdditionRateSource } {
  const itemRate = normalizeSelfDevelopedAdditionRate(args.itemRate);
  if (itemRate !== null) return { rate: itemRate, source: 'item' };
  const overrideRate = normalizeSelfDevelopedAdditionRate(args.overrideRate);
  if (overrideRate !== null) return { rate: overrideRate, source: 'override' };
  return {
    rate: resolveSelfDevelopedAdditionRate(args.set, args.categoryId),
    source: 'set',
  };
}

/**
 * 報酬計算機の経費内訳 (v3.2.13)。
 *
 * 売上総額・合計報酬額・各経費 (固定/流動/その他/手数料) と最終利益を計算。
 * 経費率はすべて「合計報酬額」に対する割合。手数料は固定額。
 *
 * - fixedRate / variableRate / otherRate: 0–100 の整数 (% 表記)
 * - fee: 円単位の手数料
 *
 * 利益マイナス時もそのまま負値で返す (UI 側で警告表示)。
 */
export interface CommissionBreakdown {
  grossRevenue: number;
  commissionTotal: number;
  fixedCost: number;
  variableCost: number;
  otherCost: number;
  fee: number;
  netProfit: number;
  profitRate: number;
}

export function calculateCommissionBreakdown(args: {
  grossRevenue: number;
  commissionTotal: number;
  fixedRate: number;
  variableRate: number;
  otherRate: number;
  fee: number;
}): CommissionBreakdown {
  const { grossRevenue, commissionTotal, fixedRate, variableRate, otherRate, fee } = args;
  const fixedCost = Math.round((commissionTotal * fixedRate) / 100);
  const variableCost = Math.round((commissionTotal * variableRate) / 100);
  const otherCost = Math.round((commissionTotal * otherRate) / 100);
  const feeRounded = Math.round(fee);
  const netProfit = commissionTotal - fixedCost - variableCost - otherCost - feeRounded;
  const profitRate =
    commissionTotal > 0
      ? Math.max(
          0,
          100 - fixedRate - variableRate - otherRate - (feeRounded / commissionTotal) * 100,
        )
      : 0;
  return {
    grossRevenue,
    commissionTotal,
    fixedCost,
    variableCost,
    otherCost,
    fee: feeRounded,
    netProfit,
    profitRate,
  };
}
